/**
 * Hooks de transacciones de recaudación (Pronto Pago) — Fase 6.2
 *
 * Todas las operaciones van vía `api/` (axios) porque el listado usa
 * service_role (operador puede ver todo) y las mutaciones son RPCs.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { QK_CUPONES } from './useCupones';
import { QK_PAGOS } from './usePagos';
import type {
  TransaccionRecaudacion,
  EstadoTransaccion,
  ConfirmarTrxPayload,
  RechazarTrxPayload,
  ConfirmarTrxResult,
  SimularWebhookPayload,
  SimularWebhookResult,
} from '../types/transaccion';

export const QK_TRX = {
  all:  ['transacciones'] as const,
  list: (estado?: EstadoTransaccion, empresaId?: string) =>
    ['transacciones', 'list', estado ?? 'all', empresaId ?? 'all'] as const,
  detail: (id?: string) => ['transacciones', 'detail', id] as const,
};

// ── LIST ──────────────────────────────────────────────────────────────────────

export interface TrxFiltros {
  estado?: EstadoTransaccion;
  empresaId?: string;
}

export function useTransacciones(filtros: TrxFiltros = {}) {
  return useQuery({
    queryKey: QK_TRX.list(filtros.estado, filtros.empresaId),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filtros.estado)    params.set('estado', filtros.estado);
      if (filtros.empresaId) params.set('empresa_id', filtros.empresaId);
      const qs = params.toString();
      const { data } = await api.get<{ data: TransaccionRecaudacion[] }>(
        `/transacciones${qs ? `?${qs}` : ''}`,
      );
      return data.data;
    },
  });
}

export function useTransaccion(id: string | undefined) {
  return useQuery({
    queryKey: QK_TRX.detail(id),
    queryFn: async () => {
      const { data } = await api.get<{ data: TransaccionRecaudacion }>(
        `/transacciones/${id}`,
      );
      return data.data;
    },
    enabled: !!id,
  });
}

// ── CONFIRMAR ─────────────────────────────────────────────────────────────────

function invalidarTodo(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: QK_TRX.all });
  qc.invalidateQueries({ queryKey: QK_PAGOS.all });
  qc.invalidateQueries({ queryKey: QK_CUPONES.all });
}

export function useConfirmarTransaccion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, cupon_id, empresa_id }: ConfirmarTrxPayload) => {
      const { data } = await api.post<{ data: ConfirmarTrxResult }>(
        `/transacciones/${id}/confirmar`,
        { cupon_id, empresa_id },
      );
      return data.data;
    },
    onSuccess: () => invalidarTodo(qc),
  });
}

export function useRechazarTransaccion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, motivo }: RechazarTrxPayload) => {
      const { data } = await api.post<{ data: unknown }>(
        `/transacciones/${id}/rechazar`,
        { motivo },
      );
      return data.data;
    },
    onSuccess: () => invalidarTodo(qc),
  });
}

// ── SIMULADOR (dev) ───────────────────────────────────────────────────────────

export function useSimularWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SimularWebhookPayload) => {
      const { data } = await api.post<SimularWebhookResult>(
        '/dev/simular-webhook-pronto-pago',
        payload,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_TRX.all }),
  });
}

// ── Cupones disponibles para conciliar (de una empresa) ───────────────────────
// Lectura directa a Supabase con join — los cupones con estado 'pendiente'
// son los candidatos a recibir un pago.

import { supabase } from '../services/supabase';
import type { Cupon } from '../types/cupon';

export function useCuponesPendientesDeEmpresa(empresaId: string | undefined) {
  return useQuery({
    queryKey: ['cupones', 'pendientes-by-empresa', empresaId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cupones')
        .select(
          '*, empresa:empresas(id, razon_social, cuit), periodo:periodos(id, periodo, descripcion)',
        )
        .eq('empresa_id', empresaId!)
        .eq('estado', 'pendiente')
        .order('fecha_vencimiento', { ascending: true });
      if (error) throw error;
      return (data ?? []) as Cupon[];
    },
    enabled: !!empresaId,
  });
}
